import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const Empty = styled.div`
  padding: 1rem 0.75rem;
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
`;

const SpinnerEl = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  vertical-align: middle;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.75rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const SkelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.5rem;
  padding-left: ${({ depth }) => 0.5 + (depth || 0) * 1.0}rem;
`;

const SkelBar = styled.div`
  height: 12px;
  width: ${({ w }) => w || '100%'};
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 35%, #f1f5f9 70%);
  background-size: 600px 100%;
  border-radius: 4px;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkelDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 35%, #f1f5f9 70%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  padding-left: ${({ depth }) => 0.5 + (depth || 0) * 1.0}rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ active }) => (active ? '#1d4ed8' : '#374151')};
  background: ${({ active }) => (active ? '#eff6ff' : 'transparent')};
  transition: background 0.12s ease;

  &:hover {
    background: ${({ active }) => (active ? '#dbeafe' : '#f9fafb')};
  }
`;

const Caret = styled.button`
  width: 18px;
  height: 18px;
  padding: 0;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;

  & svg {
    width: 12px;
    height: 12px;
    transform: rotate(${({ open }) => (open ? '90deg' : '0deg')});
    transition: transform 0.15s ease;
  }
`;

const FolderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ active }) => (active ? '#2563eb' : '#fbbf24')};
  flex-shrink: 0;

  & svg {
    width: 16px;
    height: 16px;
  }
`;

const Label = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FolderSvg = ({ open }) => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    {open ? (
      <path d="M2 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v1H4l-2 9V7z" />
    ) : (
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    )}
  </svg>
);

function buildTree(allKeys) {
  const root = {};
  for (const key of allKeys) {
    const parts = key.split('/').filter(Boolean);
    if (parts.length <= 1) continue;
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i];
      if (!node[seg]) node[seg] = { __children: {} };
      node = node[seg].__children;
    }
  }
  return root;
}

function nodeToList(node) {
  return Object.keys(node).sort((a, b) => a.localeCompare(b));
}

const TreeNode = ({ name, path, children, depth, expanded, onToggle, currentPath, onSelect }) => {
  const hasChildren = Object.keys(children).length > 0;
  const isOpen = !!expanded[path];
  const isActive = currentPath === path;

  return (
    <>
      <Row
        depth={depth}
        active={isActive}
        onClick={() => onSelect(path)}
      >
        {hasChildren ? (
          <Caret
            open={isOpen}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(path);
            }}
            aria-label={isOpen ? '접기' : '펼치기'}
          >
            <ChevronRight />
          </Caret>
        ) : (
          <span style={{ width: 18, flexShrink: 0 }} />
        )}
        <FolderIcon active={isActive}>
          <FolderSvg open={isOpen || isActive} />
        </FolderIcon>
        <Label>{name}</Label>
      </Row>
      {hasChildren && isOpen &&
        nodeToList(children).map((childName) => {
          const childPath = path ? `${path}/${childName}` : childName;
          return (
            <TreeNode
              key={childPath}
              name={childName}
              path={childPath}
              children={children[childName].__children}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              currentPath={currentPath}
              onSelect={onSelect}
            />
          );
        })
      }
    </>
  );
};

const FolderTree = ({ selectedRemote, currentPath, setCurrentPath, refreshKey, onStats }) => {
  const [allKeys, setAllKeys] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!selectedRemote) {
      setAllKeys([]);
      if (onStats) onStats(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(selectedRemote.fileList);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const files = data.files || [];
      setAllKeys(files.map((f) => f.key || f.name).filter(Boolean));
      if (onStats) {
        const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
        onStats({
          fileCount: typeof data.count === 'number' ? data.count : files.length,
          totalSize,
        });
      }
    } catch (e) {
      console.error('FolderTree fetch error', e);
      setError(e.message);
      if (onStats) onStats(null);
    } finally {
      setLoading(false);
    }
  }, [selectedRemote, onStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  const tree = useMemo(() => buildTree(allKeys), [allKeys]);

  // 현재 경로에 따라 상위 폴더들을 자동 expand
  useEffect(() => {
    if (!currentPath) return;
    const segs = currentPath.split('/').filter(Boolean);
    setExpanded((prev) => {
      const next = { ...prev };
      let acc = '';
      for (const s of segs) {
        acc = acc ? `${acc}/${s}` : s;
        next[acc] = true;
      }
      return next;
    });
  }, [currentPath]);

  const handleToggle = (path) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  if (!selectedRemote) {
    return <Empty>원격지를 먼저 선택하세요</Empty>;
  }

  if (loading && allKeys.length === 0) {
    return (
      <Wrap>
        <LoadingWrap><SpinnerEl /> 폴더 불러오는 중</LoadingWrap>
        {Array.from({ length: 7 }).map((_, i) => (
          <SkelRow key={i} depth={i % 3 === 0 ? 0 : 1}>
            <SkelDot />
            <SkelDot />
            <SkelBar w={`${50 + ((i * 23) % 100)}px`} />
          </SkelRow>
        ))}
      </Wrap>
    );
  }

  if (error) {
    return <Empty>불러오기 실패: {error}</Empty>;
  }

  const topNames = nodeToList(tree);
  const driveLabel = selectedRemote.name + (selectedRemote.type ? ` (${selectedRemote.type})` : '');
  const isRootActive = !currentPath;
  const rootOpen = expanded['__root__'] !== false; // 기본 펼침

  return (
    <Wrap>
      <Row depth={0} active={isRootActive} onClick={() => setCurrentPath('')}>
        <Caret
          open={rootOpen}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => ({ ...prev, __root__: !rootOpen }));
          }}
          aria-label={rootOpen ? '접기' : '펼치기'}
        >
          <ChevronRight />
        </Caret>
        <FolderIcon active={isRootActive}>
          <FolderSvg open={rootOpen || isRootActive} />
        </FolderIcon>
        <Label>{driveLabel}</Label>
      </Row>
      {rootOpen && topNames.map((name) => (
        <TreeNode
          key={name}
          name={name}
          path={name}
          children={tree[name].__children}
          depth={1}
          expanded={expanded}
          onToggle={handleToggle}
          currentPath={currentPath}
          onSelect={setCurrentPath}
        />
      ))}
      {topNames.length === 0 && <Empty>폴더가 없습니다</Empty>}
    </Wrap>
  );
};

export default FolderTree;
